import type { AchievementView, GameUiLook, UiScreen, UiView } from '@mogzo/shared';
import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { type ChatLookName, ChatNote, CompactChat, type MatchChat, MatchChatPanel } from './chat';
import { cornersCss, type Device, uiTokens } from './look';
import { type MatchOpen, MatchPanel, RoomPanel } from './match';
import {
  AchievementIcon,
  AchievementsPanel,
  type BoardPeriod,
  type BoardViewName,
  LeaderboardPanel,
  type OverlayData,
  type PauseChatRow,
  PauseMenu,
  type PlayerSettings,
  SignInPrompt,
} from './panels';

/** Which panel is open; `back` returns to the pause menu when it closes. */
export type PanelRequest =
  | { kind: 'leaderboard'; id?: string; view?: BoardViewName; period?: BoardPeriod; back?: boolean }
  | { kind: 'achievements'; back?: boolean }
  | { kind: 'pause' }
  | { kind: 'signin' }
  | { kind: 'match'; open: MatchOpen }
  /**
   * Realtime rooms (M35): "Reconnecting…" while a dropped connection gets its place back, "You
   * left the room" when it didn't, and "Multiplayer is busy" when no room could open.
   */
  | { kind: 'room'; show: 'reconnecting'; until: number }
  | { kind: 'room'; show: 'lost' | 'busy' };

/**
 * A short entrance (rise and fade) as the element appears, unless the player asked for reduced
 * motion. Done with the Web Animations API: mogzo.com's security policy allows no inline
 * <style>.
 */
function useEntrance<T extends HTMLElement>(key: unknown, rise = 10) {
  const ref = useRef<T>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: replays when `key` changes
  useEffect(() => {
    const element = ref.current;
    if (!element?.animate) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    element.animate(
      [
        { opacity: 0, translate: `0 ${rise}px` },
        { opacity: 1, translate: '0 0' },
      ],
      { duration: 220, easing: 'ease-out' },
    );
  }, [key]);
  return ref;
}

/** Unlocked achievements, shown one after another for the look's number of seconds. */
export function useToastQueue(seconds: number) {
  const [queue, setQueue] = useState<AchievementView[]>([]);
  const toast = queue[0] ?? null;
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setQueue((q) => q.slice(1)), seconds * 1000);
    return () => clearTimeout(timer);
  }, [toast, seconds]);
  const push = useCallback(
    (achievement: AchievementView) => setQueue((q) => [...q, achievement]),
    [],
  );
  return { toast, push };
}

/**
 * MOGZO's panels over a game, in the game's look (M19). Lay it over the game's frame (it fills
 * its parent); it only catches clicks while a panel is open. `device` picks sheets (phone) or
 * side panels and cards (tablet and computer); with `screen`, it picks the view whose values
 * each panel is drawn with.
 */
export function GameUiLayer({
  look,
  device,
  screen = 'game',
  gameName,
  panel,
  toast,
  data,
  settings,
  onSettings,
  onOpen,
  onClose,
  onRestart,
  onExit,
  signInUrl,
  signUpUrl,
  besidePauseButton = false,
  autoFocus = true,
  chat = null,
  chatOpen = null,
  onChatOpen,
  chatUnread = 0,
  messages = null,
}: {
  look: GameUiLook;
  device: Device;
  screen?: UiScreen;
  gameName: string;
  panel: PanelRequest | null;
  toast: AchievementView | null;
  data: OverlayData;
  settings: PlayerSettings;
  onSettings: (next: PlayerSettings) => void;
  onOpen: (panel: PanelRequest) => void;
  /** The panel closed; the multiplayer panel passes what the player did (a MatchOutcome). */
  onClose: (result?: unknown) => void;
  onRestart?: () => void;
  onExit?: () => void;
  signInUrl: string;
  signUpUrl: string;
  /** On a phone, leave room at the top left for MOGZO's pause button. */
  besidePauseButton?: boolean;
  /** Move focus into a panel when it opens (not in the dashboard's preview, where you're editing). */
  autoFocus?: boolean;
  /** The chat of the match the game is in (M38b), if its mode has chat. */
  chat?: MatchChat | null;
  /** The look the chat is open in, if it's open. */
  chatOpen?: ChatLookName | null;
  onChatOpen?: (look: ChatLookName | null) => void;
  /** The match's new messages since its chat was last open, for the pause menu. */
  chatUnread?: number;
  /** The player's own messages, for the pause menu (mogzo.com). */
  messages?: PauseChatRow | null;
}) {
  const view: UiView = `${device}.${screen}`;
  // The room panels (M35) are drawn with the Multiplayer panel's look.
  const t = uiTokens(look, view, panel?.kind === 'room' ? 'match' : (panel?.kind ?? 'theme'));
  const pop = uiTokens(look, view, 'popup');
  const box = useRef<HTMLDivElement>(null);
  const toastRef = useEntrance<HTMLDivElement>(toast?.id, pop.values.edge === 'top' ? -12 : 12);
  const panelRef = useEntrance<HTMLElement>(panel?.kind);
  const phone = device === 'phone';
  // The multiplayer and room panels close only with their own buttons.
  const close = () =>
    panel?.kind === 'match' || panel?.kind === 'room'
      ? undefined
      : panel && 'back' in panel && panel.back
        ? onOpen({ kind: 'pause' })
        : onClose();

  // Focus moves into a panel when it opens, so keyboards and screen readers land there.
  useEffect(() => {
    if (!panel || !autoFocus) return;
    const target =
      box.current?.querySelector<HTMLElement>('[data-autofocus]') ??
      box.current?.querySelector<HTMLElement>('button, a[href], select');
    target?.focus({ preventScroll: true });
  }, [panel, autoFocus]);

  const wide = panel?.kind === 'leaderboard' || panel?.kind === 'achievements';
  // A phone's sheets slide up from the bottom, so their bottom corners stay square.
  const sheetEnd: CSSProperties = {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: cornersCss({ ...t.corners, br: 0, bl: 0 }),
    paddingBottom: `calc(${t.padding}px + env(safe-area-inset-bottom, 0px))`,
  };
  const placement: CSSProperties = phone
    ? wide
      ? { ...sheetEnd, height: `${t.values.height}%` }
      : { ...sheetEnd, maxHeight: '92%', overflowY: 'auto' }
    : wide
      ? {
          position: 'absolute',
          top: 12,
          right: 12,
          bottom: 12,
          width: `min(${t.values.width}px, calc(100% - 24px))`,
          borderRadius: cornersCss(t.corners),
        }
      : {
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: `min(${t.values.width}px, calc(100% - 32px))`,
          maxHeight: 'calc(100% - 24px)',
          overflowY: 'auto',
          borderRadius: cornersCss(t.corners),
        };

  const edge = pop.values.edge;
  const inset = 12 + pop.values.offset;
  const toastPlace: CSSProperties = phone
    ? edge === 'top'
      ? {
          top: `calc(env(safe-area-inset-top, 0px) + ${inset}px)`,
          left: besidePauseButton ? 68 : 12,
          right: 12,
        }
      : { bottom: `calc(env(safe-area-inset-bottom, 0px) + ${inset + 4}px)`, left: 12, right: 12 }
    : {
        [edge]: inset,
        left: '50%',
        transform: 'translateX(-50%)',
        width: `min(${pop.values.width}px, calc(100% - 24px))`,
      };

  return (
    <div
      ref={box}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', fontFamily: t.font }}
    >
      {toast && settings.popups && !panel && (
        <div
          ref={toastRef}
          role="status"
          style={{
            position: 'absolute',
            ...toastPlace,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: Math.max(6, pop.padding - 8),
            paddingRight: Math.max(10, pop.padding - 4),
            borderRadius: cornersCss(pop.corners),
            background: pop.colours.panel,
            color: pop.colours.text,
            fontSize: pop.base,
            lineHeight: 1.3,
            boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
          }}
        >
          <AchievementIcon t={pop} achievement={{ ...toast, unlocked: true }} size={44} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                display: 'block',
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: pop.colours.muted,
              }}
            >
              Achievement unlocked
            </span>
            <strong style={{ display: 'block', fontSize: '1.1em' }}>{toast.name}</strong>
          </span>
          {toast.points > 0 && (
            <span
              style={{
                flex: 'none',
                padding: '5px 10px',
                borderRadius: pop.buttonRadius,
                background: pop.colours.accent,
                color: pop.colours.onAccent,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              +{toast.points}
            </span>
          )}
        </div>
      )}

      {chat &&
        !panel &&
        (chat.look === 'compact' || chatOpen === 'compact') &&
        chatOpen !== 'panel' && (
          <CompactChat
            chat={chat}
            typing={chatOpen === 'compact'}
            onTyping={(on) => onChatOpen?.(on ? 'compact' : null)}
            showLines={settings.chat !== false}
            avoidPauseButton={phone && besidePauseButton}
          />
        )}
      {chat && !panel && chatOpen === 'panel' && (
        <MatchChatPanel
          t={uiTokens(look, view, 'theme')}
          chat={chat}
          phone={phone}
          onClose={() => onChatOpen?.(null)}
        />
      )}
      {chat && !panel && phone && chat.look === 'panel' && !chatOpen && settings.chat !== false && (
        <ChatNote chat={chat} avoidPauseButton={besidePauseButton} />
      )}
      {panel && (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'auto' }}>
          <div
            aria-hidden="true"
            onClick={close}
            style={{ position: 'absolute', inset: 0, background: t.dim }}
          />
          <section
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mogzo-panel-title"
            style={{ ...t.panel, ...placement }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                // Handled here: the page's own Esc (which opens the pause menu) leaves it alone.
                event.preventDefault();
                event.stopPropagation();
                close();
              }
            }}
          >
            {phone && (
              <span
                aria-hidden="true"
                style={{
                  alignSelf: 'center',
                  flex: 'none',
                  width: 40,
                  height: 5,
                  marginTop: -6,
                  borderRadius: 3,
                  background: t.colours.line,
                }}
              />
            )}
            {panel.kind === 'leaderboard' && (
              <LeaderboardPanel t={t} data={data} initial={panel} onClose={close} />
            )}
            {panel.kind === 'achievements' && (
              <AchievementsPanel t={t} data={data} onClose={close} />
            )}
            {panel.kind === 'pause' && (
              <PauseMenu
                t={t}
                gameName={gameName}
                settings={settings}
                onSettings={onSettings}
                onResume={onClose}
                onOpen={(kind) => onOpen({ kind, back: true })}
                onRestart={onRestart}
                onExit={onExit}
                matchChat={
                  chat
                    ? {
                        unread: chatUnread,
                        open: () => {
                          onClose();
                          onChatOpen?.(chat.look);
                        },
                      }
                    : null
                }
                messages={messages}
              />
            )}
            {panel.kind === 'signin' && (
              <SignInPrompt
                t={t}
                signInUrl={signInUrl}
                signUpUrl={signUpUrl}
                onClose={() => onClose()}
              />
            )}
            {panel.kind === 'room' && (
              <RoomPanel t={t} request={panel} onDone={(choice) => onClose(choice)} />
            )}
            {panel.kind === 'match' && (
              <MatchPanel
                t={t}
                data={data.multiplayer}
                open={panel.open}
                phone={phone}
                onDone={onClose}
              />
            )}
          </section>
        </div>
      )}
    </div>
  );
}
